import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Phone, Mail, Loader2 } from "lucide-react";
import { apiClient } from "../lib/api";
import { BRAND, VEHICLES } from "../lib/constants";

const STATUS_COLORS = {
  new: "bg-[#E50914] text-white",
  contacted: "bg-amber-500 text-black",
  confirmed: "bg-emerald-500 text-black",
  completed: "bg-zinc-600 text-white",
  cancelled: "bg-zinc-900 text-zinc-500 border border-zinc-800",
};

const STATUSES = ["new", "contacted", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/bookings");
      setBookings(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("sfe_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("sfe_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const updateStatus = async (id, status) => {
    try {
      const res = await apiClient.patch(`/admin/bookings/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? res.data : x)));
      if (selected?.id === id) setSelected(res.data);
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("sfe_admin_token");
    navigate("/admin/login");
  };

  const filtered = useMemo(() => {
    return bookings
      .filter((b) => (filter === "all" ? true : b.status === filter))
      .filter((b) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          b.full_name.toLowerCase().includes(q) ||
          b.email.toLowerCase().includes(q) ||
          b.phone.toLowerCase().includes(q) ||
          (b.pickup_location || "").toLowerCase().includes(q)
        );
      });
  }, [bookings, filter, search]);

  const stats = useMemo(() => {
    const total = bookings.length;
    const newCount = bookings.filter((b) => b.status === "new").length;
    const revenue = bookings
      .filter((b) => ["confirmed", "completed"].includes(b.status) && b.estimated_total)
      .reduce((acc, b) => acc + (b.estimated_total || 0), 0);
    return { total, newCount, revenue };
  }, [bookings]);

  const vehicleName = (id) => VEHICLES.find((v) => v.id === id)?.name || id;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 z-40 bg-black/95 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo-link">
            <img src={BRAND.logo} alt="SFE" className="h-9 w-9 object-contain" />
            <div className="hidden sm:block">
              <div className="font-display text-sm tracking-tight">SFE BLACK CAR</div>
              <div className="text-[9px] tracking-[0.3em] text-zinc-500">ADMIN CONTROL</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="refresh-btn"
              className="text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white border border-zinc-800 px-3 py-2 inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#E50914] border border-zinc-800 px-3 py-2 inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Total Requests" value={stats.total} data-testid="stat-total" />
          <Stat label="New / Pending" value={stats.newCount} accent data-testid="stat-new" />
          <Stat
            label="Pipeline Revenue"
            value={`$${stats.revenue.toLocaleString()}`}
            data-testid="stat-revenue"
          />
          <Stat
            label="Avg Booking"
            value={
              stats.total
                ? `$${Math.round(
                    bookings.reduce((a, b) => a + (b.estimated_total || 0), 0) / stats.total
                  ).toLocaleString()}`
                : "$0"
            }
            data-testid="stat-avg"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              placeholder="Search by name, email, phone, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              className="w-full bg-[#0A0A0A] border border-zinc-800 focus:border-[#E50914] outline-none pl-10 pr-4 py-2.5 text-sm text-white"
            />
          </div>
          <div className="flex gap-1 bg-[#0A0A0A] border border-zinc-800 p-1">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`text-[10px] uppercase tracking-[0.2em] px-3 py-2 transition-all ${
                  filter === s ? "bg-[#E50914] text-white" : "text-zinc-500 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#0A0A0A] border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black border-b border-zinc-800">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">Date / Time</th>
                  <th className="px-4 py-3">Hrs</th>
                  <th className="px-4 py-3">Estimate</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="font-mono-data text-xs">
                {loading && (
                  <tr>
                    <td colSpan="8" className="text-center py-16 text-zinc-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-16 text-zinc-600 uppercase tracking-[0.2em] text-[11px]">
                      No bookings yet
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((b) => (
                    <tr
                      key={b.id}
                      data-testid={`booking-row-${b.id}`}
                      className="border-b border-zinc-900 hover:bg-black/40 transition-colors cursor-pointer"
                      onClick={() => setSelected(b)}
                    >
                      <td className="px-4 py-4 text-zinc-500">
                        {new Date(b.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-white">{b.full_name}</div>
                        <div className="text-zinc-500 text-[10px]">{b.phone}</div>
                      </td>
                      <td className="px-4 py-4 text-zinc-300">{vehicleName(b.vehicle)}</td>
                      <td className="px-4 py-4 text-zinc-400">
                        {b.pickup_date} · {b.pickup_time}
                      </td>
                      <td className="px-4 py-4 text-zinc-400">{b.hours || "—"}</td>
                      <td className="px-4 py-4 text-[#E50914]">
                        {b.estimated_total ? `$${b.estimated_total.toLocaleString()}` : "Quote"}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 ${STATUS_COLORS[b.status]}`}
                        >
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right text-zinc-500 text-[10px] uppercase tracking-wider">
                        View →
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="booking-detail-overlay"
        >
          <div
            className="bg-[#0A0A0A] border-l border-zinc-800 w-full max-w-xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-[#E50914] uppercase">
                    Booking Detail
                  </div>
                  <div className="font-display text-2xl uppercase tracking-tight mt-2">
                    {selected.full_name}
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-zinc-500 hover:text-white text-2xl"
                  data-testid="close-detail-btn"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <a
                  href={`tel:${selected.phone}`}
                  className="border border-zinc-800 p-4 hover:border-[#E50914] flex items-center gap-3"
                >
                  <Phone className="w-4 h-4 text-[#E50914]" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                      Phone
                    </div>
                    <div className="text-sm font-mono-data">{selected.phone}</div>
                  </div>
                </a>
                <a
                  href={`mailto:${selected.email}`}
                  className="border border-zinc-800 p-4 hover:border-[#E50914] flex items-center gap-3"
                >
                  <Mail className="w-4 h-4 text-[#E50914]" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                      Email
                    </div>
                    <div className="text-sm font-mono-data truncate">{selected.email}</div>
                  </div>
                </a>
              </div>

              <DetailRow k="Vehicle" v={vehicleName(selected.vehicle)} />
              <DetailRow k="Service Type" v={selected.service_type.replace("_", " ")} />
              <DetailRow k="Pickup" v={`${selected.pickup_date} @ ${selected.pickup_time}`} />
              <DetailRow k="Hours" v={selected.hours || "Custom Quote"} />
              <DetailRow k="Passengers" v={selected.passengers || "—"} />
              <DetailRow k="Pickup Location" v={selected.pickup_location} />
              <DetailRow k="Dropoff Location" v={selected.dropoff_location || "—"} />
              <DetailRow k="Occasion" v={selected.occasion || "—"} />
              <DetailRow
                k="Add-Ons"
                v={selected.add_ons.length ? selected.add_ons.join(", ") : "None"}
              />
              <DetailRow k="Notes" v={selected.notes || "—"} />
              <DetailRow
                k="Estimated Total"
                v={
                  selected.estimated_total
                    ? `$${selected.estimated_total.toLocaleString()}`
                    : "Custom Quote"
                }
                accent
              />

              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                  Update Status
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`update-status-${s}`}
                      disabled={selected.status === s}
                      className={`text-[10px] uppercase tracking-[0.2em] py-3 transition-all ${
                        selected.status === s
                          ? "bg-[#E50914] text-white"
                          : "border border-zinc-800 text-zinc-400 hover:border-[#E50914] hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent, ...rest }) => (
  <div
    {...rest}
    className={`bg-[#0A0A0A] border ${accent ? "border-[#E50914]" : "border-zinc-800"} p-5`}
  >
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{label}</div>
    <div className={`font-display text-3xl ${accent ? "text-[#E50914]" : "text-white"}`}>
      {value}
    </div>
  </div>
);

const DetailRow = ({ k, v, accent }) => (
  <div className="grid grid-cols-3 gap-4 py-3 border-b border-zinc-900">
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{k}</div>
    <div className={`col-span-2 text-sm ${accent ? "text-[#E50914] font-bold font-mono-data" : "text-white"}`}>
      {v}
    </div>
  </div>
);
